import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { storage, db } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  orderBy,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
} from 'chart.js';
import { FiUpload, FiVideo, FiBarChart2, FiTrash2, FiEye, FiDollarSign } from 'react-icons/fi';
import { BsGraphUp } from 'react-icons/bs';
import { RiVideoLine } from 'react-icons/ri';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
);

const Dashboard = () => {
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    file: null,
    price: '',
    thumbnail: null
  });
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('upload');
  const [analytics, setAnalytics] = useState({
    totalViews: 0,
    totalSales: 0,
    totalRevenue: 0,
    totalLikes: 0,
  });
  const [loading, setLoading] = useState(true);

  // Fetch videos
  useEffect(() => {
    if (!user) return;
    
    setLoading(true);
    const q = query(
      collection(db, 'videos'),
      where('userEmail', '==', user.email),
      orderBy('uploadedAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const videoData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setVideos(videoData);
        setLoading(false);
      },
      (err) => {
        console.error('Firestore error:', err);
        toast.error('Failed to load videos');
        setLoading(false);
      }
    );
    
    return () => unsubscribe();
  }, [user]);

  // Fetch analytics data
  useEffect(() => {
    if (!user) return;
    
    const fetchAnalytics = async () => {
      try {
        // In a real app, you would fetch this data from Firestore
        // This is mock data for demonstration
        const mockAnalytics = {
          totalViews: 1500 + Math.floor(Math.random() * 500),
          totalSales: 50 + Math.floor(Math.random() * 20),
          totalRevenue: 2499.5 + Math.floor(Math.random() * 1000),
          totalLikes: 200 + Math.floor(Math.random() * 100),
        };
        
        setAnalytics(mockAnalytics);
      } catch (err) {
        console.error('Error fetching analytics:', err);
        toast.error('Failed to load analytics data');
      }
    };
    
    fetchAnalytics();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    if (!formData.title || !formData.description || !formData.file || !formData.price) {
      toast.error('All fields are required');
      setUploading(false);
      return;
    }
    
    try {
      // Upload video file
      const videoRef = ref(storage, `videos/${user.uid}/${Date.now()}_${formData.file.name}`);
      await uploadBytes(videoRef, formData.file);
      const videoUrl = await getDownloadURL(videoRef);
      
      // Upload thumbnail if provided
      let thumbnailUrl = '';
      if (formData.thumbnail) {
        const thumbnailRef = ref(storage, `thumbnails/${user.uid}/${Date.now()}_${formData.thumbnail.name}`);
        await uploadBytes(thumbnailRef, formData.thumbnail);
        thumbnailUrl = await getDownloadURL(thumbnailRef);
      }
      
      // Save video data to Firestore
      await addDoc(collection(db, 'videos'), {
        title: formData.title,
        description: formData.description,
        fileName: formData.file.name,
        url: videoUrl,
        thumbnailUrl: thumbnailUrl || null,
        userEmail: user.email,
        userId: user.uid,
        uploadedAt: new Date().toISOString(),
        price: parseFloat(formData.price),
        views: 0,
        likes: 0,
        purchases: 0
      });
      
      toast.success('Video uploaded successfully!');
      setFormData({ title: '', description: '', file: null, price: '', thumbnail: null });
      e.target.reset();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (videoId) => {
    if (window.confirm('Are you sure you want to delete this video? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'videos', videoId));
        toast.success('Video deleted successfully!');
      } catch (err) {
        console.error('Delete error:', err);
        toast.error('Failed to delete video');
      }
    }
  };

  // Chart data
  const viewData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Video Views',
        data: [300, 450, 600, 500, 650, 800],
        borderColor: '#4F46E5',
        backgroundColor: 'rgba(79, 70, 229, 0.2)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const salesData = {
    labels: videos.slice(0, 5).map((v) => v.title.length > 15 ? `${v.title.substring(0, 15)}...` : v.title),
    datasets: [
      {
        label: 'Sales',
        data: videos.slice(0, 5).map(v => Math.floor(Math.random() * 50) + 10),
        backgroundColor: [
          'rgba(79, 70, 229, 0.7)',
          'rgba(99, 102, 241, 0.7)',
          'rgba(129, 140, 248, 0.7)',
          'rgba(165, 180, 252, 0.7)',
          'rgba(199, 210, 254, 0.7)',
        ],
        borderColor: [
          'rgba(79, 70, 229, 1)',
          'rgba(99, 102, 241, 1)',
          'rgba(129, 140, 248, 1)',
          'rgba(165, 180, 252, 1)',
          'rgba(199, 210, 254, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const revenueData = {
    labels: ['Direct', 'Referral', 'Social'],
    datasets: [
      {
        label: 'Revenue Sources',
        data: [65, 25, 10],
        backgroundColor: [
          'rgba(79, 70, 229, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(236, 72, 153, 0.7)',
        ],
        borderColor: [
          'rgba(79, 70, 229, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(236, 72, 153, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 p-6 hidden md:block">
        <div className="flex items-center space-x-2 mb-8">
          <BsGraphUp className="text-indigo-600 text-2xl" />
          <h2 className="text-xl font-bold text-gray-800">Eunbro Analytics</h2>
        </div>
        <nav className="space-y-2">
          <button
            onClick={() => setActiveTab('upload')}
            className={`w-full flex items-center space-x-3 text-left py-3 px-4 rounded-lg transition-colors ${
              activeTab === 'upload' 
                ? 'bg-indigo-50 text-indigo-700 font-medium' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FiUpload className="text-lg" />
            <span>Upload Video</span>
          </button>
          <button
            onClick={() => setActiveTab('gallery')}
            className={`w-full flex items-center space-x-3 text-left py-3 px-4 rounded-lg transition-colors ${
              activeTab === 'gallery' 
                ? 'bg-indigo-50 text-indigo-700 font-medium' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FiVideo className="text-lg" />
            <span>Video Gallery</span>
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`w-full flex items-center space-x-3 text-left py-3 px-4 rounded-lg transition-colors ${
              activeTab === 'analytics' 
                ? 'bg-indigo-50 text-indigo-700 font-medium' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FiBarChart2 className="text-lg" />
            <span>Analytics</span>
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 overflow-hidden">
        <header className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Welcome back, {user?.email?.split('@')[0]}
              </h1>
              <p className="text-gray-600 mt-1">
                {activeTab === 'upload' && 'Upload new content for your audience'}
                {activeTab === 'gallery' && 'Manage your video content'}
                {activeTab === 'analytics' && 'Track your performance metrics'}
              </p>
            </div>
            <div className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg text-sm font-medium">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
          </div>
        </header>

        <div className="h-full overflow-y-auto pb-10">
          {activeTab === 'upload' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-800">
                  Upload New Video
                </h2>
                <span className="text-sm text-gray-500">
                  All fields are required
                </span>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="title"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Video Title
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="My Awesome Video"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="price"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Price (USD)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiDollarSign className="text-gray-400" />
                      </div>
                      <input
                        type="number"
                        id="price"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0"
                        className="w-full pl-8 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="9.99"
                        required
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    rows="4"
                    placeholder="Describe your video content in detail..."
                    required
                  ></textarea>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="file"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Video File
                    </label>
                    <div className="flex items-center justify-center w-full">
                      <label className="flex flex-col w-full h-32 border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                        <div className="flex flex-col items-center justify-center pt-7">
                          <RiVideoLine className="text-3xl text-gray-400" />
                          <p className="pt-1 text-sm text-gray-600">
                            {formData.file ? formData.file.name : 'Click to upload video'}
                          </p>
                        </div>
                        <input
                          type="file"
                          id="file"
                          name="file"
                          accept="video/*"
                          onChange={handleInputChange}
                          className="opacity-0"
                          required
                        />
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <label
                      htmlFor="thumbnail"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Thumbnail (Optional)
                    </label>
                    <div className="flex items-center justify-center w-full">
                      <label className="flex flex-col w-full h-32 border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                        <div className="flex flex-col items-center justify-center pt-7">
                          <svg
                            className="w-8 h-8 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            ></path>
                          </svg>
                          <p className="pt-1 text-sm text-gray-600">
                            {formData.thumbnail ? formData.thumbnail.name : 'Click to upload thumbnail'}
                          </p>
                        </div>
                        <input
                          type="file"
                          id="thumbnail"
                          name="thumbnail"
                          accept="image/*"
                          onChange={handleInputChange}
                          className="opacity-0"
                        />
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={uploading}
                    className={`w-full py-3 px-6 rounded-lg text-white font-semibold transition-colors flex items-center justify-center ${
                      uploading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    {uploading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FiUpload className="mr-2" />
                        Upload Video
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-800">
                  Your Video Content
                </h2>
                <span className="text-sm text-gray-500">
                  {videos.length} {videos.length === 1 ? 'video' : 'videos'} total
                </span>
              </div>
              
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                </div>
              ) : videos.length === 0 ? (
                <div className="text-center py-12">
                  <RiVideoLine className="mx-auto text-4xl text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">
                    No videos uploaded yet
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by uploading your first video.
                  </p>
                  <button
                    onClick={() => setActiveTab('upload')}
                    className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <FiUpload className="-ml-1 mr-2 h-5 w-5" />
                    Upload Video
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {videos.map((video) => (
                    <div
                      key={video.id}
                      className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <div className="relative pb-[56.25%] bg-gray-100">
                        {video.thumbnailUrl ? (
                          <img
                            src={video.thumbnailUrl}
                            alt={video.title}
                            className="absolute h-full w-full object-cover"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                            <RiVideoLine className="text-4xl text-gray-400" />
                          </div>
                        )}
                        <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                          {Math.floor(Math.random() * 10) + 1}m ago
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex justify-between items-start">
                          <h3 className="text-lg font-semibold text-gray-800 truncate">
                            {video.title}
                          </h3>
                          <span className="text-indigo-600 font-bold whitespace-nowrap ml-2">
                            ${video.price.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                          {video.description}
                        </p>
                        <div className="mt-4 flex justify-between items-center">
                          <div className="flex space-x-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {Math.floor(Math.random() * 1000)} views
                            </span>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {Math.floor(Math.random() * 100)} sales
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(video.uploadedAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="mt-4 flex space-x-2">
                          <a
                            href={video.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <FiEye className="-ml-1 mr-2 h-5 w-5 text-gray-400" />
                            View
                          </a>
                          <button
                            onClick={() => handleDelete(video.id)}
                            className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <FiTrash2 className="-ml-1 mr-2 h-5 w-5" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              {/* Analytics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-indigo-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Total Views</p>
                      <p className="text-2xl font-semibold text-gray-900 mt-1">{analytics.totalViews.toLocaleString()}</p>
                    </div>
                    <div className="p-3 rounded-full bg-indigo-100 text-indigo-600">
                      <FiEye className="h-6 w-6" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    <span className="text-green-600 font-medium">↑ 12.5%</span> from last month
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-green-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Total Sales</p>
                      <p className="text-2xl font-semibold text-gray-900 mt-1">{analytics.totalSales.toLocaleString()}</p>
                    </div>
                    <div className="p-3 rounded-full bg-green-100 text-green-600">
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                      </svg>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    <span className="text-green-600 font-medium">↑ 8.2%</span> from last month
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-purple-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Total Revenue</p>
                      <p className="text-2xl font-semibold text-gray-900 mt-1">${analytics.totalRevenue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                    </div>
                    <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                      <FiDollarSign className="h-6 w-6" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    <span className="text-green-600 font-medium">↑ 15.3%</span> from last month
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-pink-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Total Likes</p>
                      <p className="text-2xl font-semibold text-gray-900 mt-1">{analytics.totalLikes.toLocaleString()}</p>
                    </div>
                    <div className="p-3 rounded-full bg-pink-100 text-pink-600">
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                      </svg>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    <span className="text-green-600 font-medium">↑ 5.7%</span> from last month
                  </p>
                </div>
              </div>
              
              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Views Over Time
                  </h3>
                  <div className="h-80">
                    <Line
                      data={viewData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'top',
                          },
                          tooltip: {
                            mode: 'index',
                            intersect: false,
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            grid: {
                              drawBorder: false,
                            },
                          },
                          x: {
                            grid: {
                              display: false,
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Top Performing Videos
                  </h3>
                  <div className="h-80">
                    <Bar
                      data={salesData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'top',
                          },
                          tooltip: {
                            mode: 'index',
                            intersect: false,
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            grid: {
                              drawBorder: false,
                            },
                          },
                          x: {
                            grid: {
                              display: false,
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-sm lg:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Recent Activity
                  </h3>
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex items-start pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                        <div className="flex-shrink-0 mt-1">
                          <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                            <svg className="h-5 w-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            New purchase from user_{Math.floor(Math.random() * 1000)}
                          </p>
                          <p className="text-sm text-gray-500">
                            Purchased "{videos[Math.floor(Math.random() * videos.length)]?.title || 'Your Video'}" for ${(Math.random() * 20 + 5).toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {Math.floor(Math.random() * 60)} minutes ago
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Revenue Sources
                  </h3>
                  <div className="h-64">
                    <Pie
                      data={revenueData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'right',
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;